import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { openAs } from "../core/oda";
import { deleteObj } from "../core/delete";
import {
  HatchLoopType,
  HatchPatternTypeClosure,
  OpenMode,
} from "../core/constants";
import { Logger } from "@/logger";

/**
 * Manages hatch-boundary associations using ODA's persistent reactor API.
 *
 * Pattern: Hatch is registered as a persistent reactor on its boundary entities.
 * When a boundary changes, we query its reactors to find associated hatches.
 */
export class HatchReactorManager {
  private static instance: HatchReactorManager;
  private module: DrawingModuleType | null = null;

  constructor(module: DrawingModuleType) {
    this.module = module;
  }

  static getInstance(module: DrawingModuleType): HatchReactorManager {
    if (!HatchReactorManager.instance) {
      HatchReactorManager.instance = new HatchReactorManager(module);
    }
    return HatchReactorManager.instance;
  }

  /**
   * Register a hatch as a persistent reactor on its boundary entity.
   * This creates a persistent link stored in the DWG file.
   */
  registerReactor(
    boundaryEntityId: DrawingModule.OdDbObjectId,
    hatchEntityId: DrawingModule.OdDbObjectId,
  ): void {
    if (!this.module) return;
    const entity = openAs(
      boundaryEntityId,
      this.module.OdDbEntity,
      OpenMode.kForWrite,
    );
    try {
      entity.addPersistentReactor(hatchEntityId);
    } finally {
      deleteObj(entity);
    }
  }

  /**
   * Remove the reactor link when hatch is deleted.
   */
  unregisterReactor(
    boundaryEntityId: DrawingModule.OdDbObjectId,
    hatchEntityId: DrawingModule.OdDbObjectId,
  ): void {
    if (!this.module) return;

    try {
      const entity = openAs(
        boundaryEntityId,
        this.module.OdDbEntity,
        OpenMode.kForWrite,
      );
      entity.removePersistentReactor(hatchEntityId);
      deleteObj(entity);
    } catch {
      // Boundary may already be deleted
    }
  }

  /**
   * Get all hatch entities that are reactors on a boundary entity.
   * Filters to only return OdDbHatch objects.
   */
  getHatchReactorsForBoundary(
    boundaryEntityId: DrawingModule.OdDbObjectId,
  ): DrawingModule.OdDbObjectId[] {
    if (!this.module) return [];

    const hatches: DrawingModule.OdDbObjectId[] = [];

    try {
      const entity = openAs(
        boundaryEntityId,
        this.module.OdDbEntity,
        OpenMode.kForRead,
      );
      const reactors = entity.getPersistentReactors();

      // Filter to only hatch entities
      for (let i = 0; i < reactors.size(); i++) {
        const reactorId = reactors.get(i);
        try {
          const reactor = reactorId.safeOpenObject(OpenMode.kForRead, false);
          if (reactor.isA().isEqualTo(this.module.OdDbHatch.desc())) {
            hatches.push(reactorId);
          }
          deleteObj(reactor);
        } catch {
          // Skip invalid reactors
        }
      }

      deleteObj(reactors);
      deleteObj(entity);
    } catch {
      // Entity may not exist
    }

    return hatches;
  }

  /**
   * Sync a hatch's geometry from its boundary.
   * Calls evaluateHatch(true) to regenerate the fill.
   */
  syncHatch(
    hatchEntityId: DrawingModule.OdDbObjectId,
    boundaryEntityId: DrawingModule.OdDbObjectId,
  ): void {
    if (!this.module) return;

    const hatch = openAs(
      hatchEntityId,
      this.module.OdDbHatch,
      OpenMode.kForWrite,
    );

    try {
      hatch.removeLoopAt(0);
      const entityIds = new this.module.OdDbObjectIdArray();
      entityIds.push_back(boundaryEntityId);

      // Append loop from entity
      // Using appendLoop2 which takes entity IDs
      hatch.appendLoop2(
        HatchLoopType.kExternal as any,
        entityIds,
        HatchPatternTypeClosure.kSolidClosure,
      );
      deleteObj(entityIds);

      // Boundary edits (dragging a vertex, transforming the shape) can leave
      // ODA with a boundary it can't fill (self-intersecting, degenerate,
      // etc.) — evaluateHatch throws a native exception in that case. Skip the
      // resync rather than crashing the interaction; the boundary edit itself
      // still succeeds, just without an updated fill.
      hatch.evaluateHatch(true);
      hatch.recordGraphicsModified(true);
    } catch (error) {
      Logger.captureException(error, {
        operation: "HatchReactorManager.syncHatch",
      });
    } finally {
      deleteObj(hatch);
    }
  }

  /**
   * Sync all hatches that are reactors on a boundary entity.
   */
  syncHatchesForBoundary(boundaryEntityId: DrawingModule.OdDbObjectId): void {
    const hatches = this.getHatchReactorsForBoundary(boundaryEntityId);
    for (const hatchId of hatches) {
      this.syncHatch(hatchId, boundaryEntityId);
    }
  }
}
